// Postes proposés à l'onboarding : part usuelle du budget (%), priorité qui
// les favorise, exemples de dépenses (suggestions de libellés). Chaînes en
// français = clés de traduction.

export type PriorityKey = 'lieu' | 'traiteur' | 'boissons' | 'photo' | 'tenues' | 'deco' | 'musique' | 'voyage';

export interface CatalogPost {
  key: string;
  name: string;
  hint: string;
  weight: number;
  priority: PriorityKey | null;
  perGuest?: boolean; // dépense qui suit le nombre d'invités
  suggestions: string[];
}

export const CATALOG: CatalogPost[] = [
  { key: 'lieu', name: 'Lieu', hint: 'Location du domaine, ménage, hébergement sur place', weight: 16, priority: 'lieu', suggestions: ['Location du domaine', 'Ménage', 'Hébergement'] },
  { key: 'traiteur', name: 'Traiteur', hint: 'Dîner, cocktail du vin d’honneur, pièce montée, brunch', weight: 28, priority: 'traiteur', perGuest: true, suggestions: ['Dîner', 'Cocktail', 'Pièce montée', 'Brunch'] },
  { key: 'boissons', name: 'Boissons', hint: 'Vins, champagne, softs, bar à cocktails', weight: 6, priority: 'boissons', perGuest: true, suggestions: ['Vins', 'Champagne', 'Softs', 'Bar à cocktails'] },
  { key: 'photo', name: 'Photo & vidéo', hint: 'Photographe, vidéaste, photobooth', weight: 10, priority: 'photo', suggestions: ['Photographe', 'Vidéaste', 'Photobooth'] },
  { key: 'tenues', name: 'Tenues', hint: 'Robe, costume, retouches, accessoires', weight: 8, priority: 'tenues', suggestions: ['Robe', 'Costume', 'Retouches', 'Accessoires'] },
  { key: 'beaute', name: 'Beauté', hint: 'Coiffure, maquillage, essais', weight: 2, priority: 'tenues', suggestions: ['Coiffure', 'Maquillage', 'Essai'] },
  { key: 'alliances', name: 'Alliances', hint: 'Alliances, gravure', weight: 4, priority: null, suggestions: ['Alliances', 'Gravure'] },
  { key: 'musique', name: 'Musique & animation', hint: 'DJ, groupe, sono, cérémonie laïque', weight: 5, priority: 'musique', suggestions: ['DJ', 'Groupe', 'Sono'] },
  { key: 'deco', name: 'Fleurs & déco', hint: 'Fleuriste, location vaisselle & linge, achats déco', weight: 7, priority: 'deco', suggestions: ['Fleuriste', 'Location vaisselle', 'Achats déco'] },
  { key: 'papeterie', name: 'Papeterie', hint: 'Faire-part, menus, plan de table, marque-places', weight: 2, priority: null, suggestions: ['Faire-part', 'Menus', 'Marque-places'] },
  { key: 'transport', name: 'Transport', hint: 'Voiture des mariés, navette invités', weight: 2, priority: null, suggestions: ['Voiture des mariés', 'Navette'] },
  { key: 'cadeaux', name: 'Cadeaux invités', hint: 'Dragées, petits cadeaux, enfants', weight: 2, priority: null, suggestions: ['Dragées', 'Cadeaux'] },
  { key: 'voyage', name: 'Voyage de noces', hint: 'Vols, hébergement, activités', weight: 12, priority: 'voyage', suggestions: ['Vols', 'Hébergement', 'Activités'] },
  { key: 'imprevus', name: 'Imprévus', hint: 'Marge de sécurité, conseillée autour de 5 %', weight: 5, priority: null, suggestions: [] },
];

// Postes cochés par défaut
export const DEFAULT_KEYS = ['lieu', 'traiteur', 'boissons', 'photo', 'tenues', 'beaute', 'alliances', 'musique', 'deco', 'papeterie', 'imprevus'];

export const PRIORITIES: Array<{ key: PriorityKey; label: string }> = [
  { key: 'lieu', label: 'Le lieu' },
  { key: 'traiteur', label: 'La nourriture' },
  { key: 'boissons', label: 'Les boissons' },
  { key: 'photo', label: 'Les photos' },
  { key: 'tenues', label: 'Les tenues' },
  { key: 'deco', label: 'La déco' },
  { key: 'musique', label: 'La musique' },
  { key: 'voyage', label: 'Le voyage' },
];

export const MAX_PRIORITIES = 3;
// Budgets proposés selon la devise (montants ronds, à peu près équivalents à
// 15 / 25 / 35 / 50 k€) ; le 2e sert de valeur par défaut.
const PRESETS: Record<string, number[]> = {
  EUR: [15000, 25000, 35000, 50000],
  USD: [15000, 30000, 40000, 55000],
  GBP: [12000, 20000, 30000, 40000],
  CHF: [15000, 25000, 35000, 50000],
  CAD: [25000, 35000, 50000, 75000],
  AUD: [25000, 40000, 55000, 80000],
  NZD: [25000, 45000, 60000, 90000],
  SEK: [150000, 300000, 400000, 550000],
  NOK: [150000, 300000, 400000, 600000],
  DKK: [100000, 200000, 250000, 375000],
  PLN: [60000, 100000, 150000, 200000],
  CZK: [400000, 600000, 900000, 1250000],
  MAD: [150000, 250000, 400000, 550000],
};
export const budgetPresets = (currency: string) => PRESETS[currency] ?? PRESETS.EUR;
export const defaultBudget = (currency: string) => budgetPresets(currency)[1];

export const catalogPost = (key: string | null) => CATALOG.find((c) => c.key === key) ?? null;
